import React, {Fragment, useEffect, useState} from 'react';
import clsx from 'clsx';
import {makeStyles} from '@material-ui/styles';
import SwipeableViews from 'react-swipeable-views';
import {autoPlay, virtualize} from 'react-swipeable-views-utils';
import PropTypes from 'prop-types';
import CarouselDots from 'react-storefront/carousel/CarouselDots';
import CarouselArrows from 'react-storefront/carousel/CarouselArrows';
import mod from 'react-storefront/utils/mod';
import useMediaQuery from "@material-ui/core/useMediaQuery";
import theme from "../../../theme";

const styles = theme => ({
	/**
	 * Styles applied to the root element.
	 */
	root: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		position: 'relative',
		flexBasis: '100%',
		'& img': {
			display: 'block',
		},
		[theme.breakpoints.up('sm')]: {
			maxWidth: '100%',
			paddingLeft: 96,
			'&.lightboxActive': {
				paddingLeft: 0
			}
		}
	},
	/**
	 * Styles applied to wrapper element of the swipe container.
	 */
	swipeWrap: {
		position: 'relative',
		overflow: 'hidden',
		flex: 1,
		height: '100%',
		'& .react-swipeable-view-container, & .react-swipeable-view-container > div': {
			display: 'flex',
    		alignItems: 'center',
			height: '100%',
			willChange: 'auto !important'
		},
		'& .react-swipeable-view-container > div': {
			overflow: 'hidden !important'
		},
		'&:hover .arrows': {
			transform: 'scale(1)'
		}
	},
	autoPlaySwipeableViews: {
		overflowY: 'hidden',
		height: '100%',
	},
	'@media not all and (hover:none)': {
		hideTouchArrows: {
			display: 'none',
		},
	},
	arrows: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		transform: 'scale(1.5)',
		transition: 'all 0.25s ease',
		pointerEvents: 'none'
	},
	arrow: {
		padding: 14,
		backgroundColor: theme.palette.primary.background,
		pointerEvents: 'all',
		color: '#000',
		boxShadow: theme.boxShadow[1],
		'&:hover': {
			backgroundColor: theme.palette.primary.background
		}
	},
	rightArrow: {
		right: '10px'
	},
	leftArrow: {
		left: '10px'
	}
});

const dotStyles = theme => ({
	/**
	 * Styles applied to the root element.
	 */
	dots: {
		position: 'absolute',
		bottom: '20px',
		textAlign: 'center',
		width: '100%',
		[theme.breakpoints.up('sm')]: {
			display: 'none'
		}
	},

	/**
	 * Styles applied to each dot element.
	 */
	dot: {
		display: 'inline-block',
		width: 35,
		height: 2,
		margin: '0 5px',
		opacity: 0.5,
		borderRadius: 0,
		transitionDuration: '0.35s',
		backgroundColor: '#f1f1f1',
		boxShadow: '1px 1px 0px rgba(0,0,0,0.3)'
	},

	/**
	 * Styles applied to the dot representing the selected slide.
	 */
	dotSelected: {
		opacity: 1
	},
})

const useStyles = makeStyles(styles, {name: 'RSFCarousel'});
const useDotStyles = makeStyles(dotStyles, {name: 'RSFCarouselDots'});

export const VirtualizeSwipeableViews = virtualize(SwipeableViews);

function useSelected(props) {
	if (props.setSelected) {
		return props;
	} else {
		const [selected, setSelected] = useState(0);
		return {selected, setSelected};
	}
}

/**
 * A grouped display of elements that shows one element at a time, and changes to peer elements by
 * swiping to the left or right, or by clicking arrows on the sides of the component. Generally used
 * as a non-Amp option for the [`CarouselComponent`](/apiReference/carousel/MediaCarousel#prop-CarouselComponent)
 * prop within a [`MediaCarousel`](/apiReference/carousel/MediaCarousel).
 */
const Carousel = React.forwardRef((props, ref) => {
	let {
		children,
		classes,
		className,
		style,
		swipeStyle,
		slideStyle,
		arrows,
		aboveAdornments,
		belowAdornments,
		onClick,
		indicators,
		interval,
		infinite,
		lightboxActive,
	} = props;

	classes = useStyles({classes});
	const dotClasses = useDotStyles();

	let {selected, setSelected} = useSelected(props);
	const count = children.length || 0;

	let Tag = VirtualizeSwipeableViews;

	const slideRenderer = ({index, key}) => {
		const child = children[mod(index, count)];
		if (!child) return null;
		const slide = React.cloneElement(child);
		return <Fragment key={'slide-renderer-' + key}>{slide}</Fragment>;
	};

	if(selected < 0) {
		selected = count - Math.abs(selected);
	}

	return (
		<div
			ref={ref}
			className={[clsx(className, classes.root), lightboxActive ? 'lightboxActive' : ''].join(' ')}
			style={style}
			onClick={onClick}
		>
			{aboveAdornments}
			<div className={classes.swipeWrap}>
				<Tag
					index={selected}
					onChangeIndex={setSelected}
					disabled={false}
					className={classes.autoPlaySwipeableViews}
					style={swipeStyle}
					slideStyle={slideStyle}
					slideRenderer={slideRenderer}
					slideCount={count}
					interval={interval}
					containerStyle={{alignItems: 'center'}}
				/>
				{arrows !== false && (
					<CarouselArrows
						classes={{
							arrows: classes.arrows,
							arrow: classes.arrow,
							leftArrow: classes.leftArrow,
							rightArrow: classes.rightArrow
						}}
						className={[arrows === 'desktop' ? classes.hideTouchArrows : null, 'arrows'].join(' ') }
						selected={selected}
						setSelected={setSelected}
						count={count}
						infinite={infinite}
					/>
				)}
				{indicators ? <CarouselDots count={count} selected={selected % count} classes={dotClasses}/> : ''}
			</div>
			{belowAdornments}
		</div>
	);
});

Carousel.propTypes = {
	/**
	 * Override or extend the styles applied to the component. See [CSS API](#css) below for more details.
	 */
	classes: PropTypes.object,

	/**
	 * Set to `false` to hide arrows, `'desktop'` to only show them
	 * on non-touch devices, `'all'` to always show arrows.
	 */
	arrows: PropTypes.oneOf([false, 'desktop', 'all']),

	/**
	 * Nodes to render above the Carousel.
	 */
	aboveAdornments: PropTypes.arrayOf(PropTypes.element),

	/**
	 * Nodes to render below the Carousel.
	 */
	belowAdornments: PropTypes.arrayOf(PropTypes.element),

	/**
	 * If `true`, the Carousel will automatically cycle through the media elements.
	 */
	autoPlay: PropTypes.bool,

	/**
	 * If true, scrolling past the last slide will cycle back to the first
	 */
	infinite: PropTypes.bool,

	/**
	 * The interval time (in milliseconds) for [`autoPlay`](#prop-autoPlay).
	 */
	interval: PropTypes.number,
};

Carousel.defaultProps = {
	indicators: true,
	arrows: 'desktop',
	autoPlay: false,
	interval: 3000,
	infinite: true,
};

export default Carousel;
